import { Injectable } from '@angular/core';
import { NameNormalizeService } from '../shared/services/name-normalize.service';
@Injectable({
  providedIn: 'root',
})
export class ValidateService {
  constructor(private nameNormalize: NameNormalizeService) {}

  /**
   * Loosely tests the value that it's an email, returns tru if email.  Since
   * email addresses can be just about anything from unicode to not, the test
   * needs to account for all types of email addresses.
   * @param value email address
   */
  public isEmail(value: string): boolean {
    const rgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return rgx.test(value);
  }

  /**
   * Checks for illegal characters / and \
   * @param  {String} value The input value to test
   * @return {Boolean}      True if value passes test
   */

  public illegalCharacters(name: string): boolean {
    if (this.isEmpty(name)) {
      return true;
    }
    return /^[^\/\\]+$/.test(name);
  }

  /**
   * Checks for a leading space in the value
   * @param  {String} value The input value to test
   * @return {Boolean}      True if value passes test
   */
  public leadingSpace(value: string): boolean {
    if (this.isEmpty(value)) {
      return true;
    }
    return value.charAt(0) !== ' ';
  }

  /**
   * Makes sure the trailing character is not a dot or space character
   * @param  {String} value The input value to test
   * @return {Boolean}      True if value passes test
   */
  public trailingChar(value: string): boolean {
    if (this.isEmpty(value)) {
      return true;
    }
    const lastChar = value.charAt(value.length - 1);
    return !(lastChar === '.' || lastChar === ' ');
  }

  /**
   * These are a list of names that are reserved for use by the OS and/or
   * Sync.
   *
   * Windows does not like: com1, com1.txt, etc and many other special names
   *
   * The other names here are a list of names that the agent will ignore,
   * so we should ensure the web does not allow any names to be created that
   * the agent will ignore.
   *
   * @param  {String} value The input value to test
   * @return {Boolean}      True if value passes test
   */
  public reservedNames(value: string): boolean {
    if (this.isEmpty(value)) {
      return true;
    }
    return (
      !/^(CLOCK\$|PRN|AUX|NUL|CON|COM[0-9]|LPT[0-9])$/i.test(value) &&
      value.toLowerCase() !== '.syncinfo' &&
      value.toLowerCase() !== 'thumbs.db' &&
      value.toLowerCase() !== 'desktop.ini' &&
      value.toLowerCase() !== 'sync.cache' &&
      value.toLowerCase() !== '$recycle.bin' &&
      value.indexOf('.~') !== 0 &&
      value.indexOf('~') !== 0 &&
      value.indexOf('~$') === -1 &&
      !/^(PRN\.[a-z0-9]+|AUX\.[a-z0-9]+|NUL\.[a-z0-9]+|CON\.[a-z0-9]+|COM[0-9]\.[a-z0-9]+|LPT[0-9]\.[a-z0-9]+)$/i.test(
        value,
      )
    );
  }

  /**
   * Ensures the length of the value is less than or equal to a certain number
   * @param  {String}  value The input value to test
   * @param  {Number}  Maximum input length
   * @return {Boolean} True if value passes test
   */
  public maxLength(value: string, maxLength: number): boolean {
    if (this.isEmpty(value)) {
      return true;
    }
    return value.length <= maxLength;
  }

  /**
   * Ensures value is not empty
   * @param  {String} value The input value to test
   * @return {Boolean}      True if value passes test
   */
  public empty(value: string): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * If the value is uninitialized or an empty string, return true.
   * @param value boolean;
   */
  public isEmpty(value: string): boolean {
    return value === undefined || value === '' || value === null;
  }

  public isNameValid(name: string): boolean {
    return (
      this.illegalCharacters(name) &&
      this.empty(name) &&
      this.reservedNames(name) &&
      this.leadingSpace(name) &&
      this.trailingChar(name)
    );
  }

  public isDisplayNameValid(name: string): boolean {
    return name === this.stripUrls(name);
  }

  public isAlphaNumeric(value: string): boolean {
    if (this.isEmpty(value)) {
      return true;
    }
    return /^[A-Za-z0-9 ]*$/.test(value);
  }

  // PRIVATE HELPER FUNCTIONS
  // ========================

  private stripUrls(str: string): string {
    return str.replace(/\b((https?|ftp|file):\/\/|www\.)[-A-Z0-9+&@#\/%?=~_|$!:,.;]*[A-Z0-9+&@#\/%=~_|$]/i, ' ');
  }

  public hasLeadingSpace(name: string): boolean {
    return this.isEmpty(name) ? false : name.charAt(0) === ' ';
  }

  public hasTrailingChar(name: string): boolean {
    const lastChar = name ? name.charAt(name.length - 1) : '';
    return this.isEmpty(name) ? false : lastChar === '.' || lastChar === ' ';
  }

  public hasReservedNames(name: string): boolean {
    return this.isEmpty(name)
      ? false
      : /^(CLOCK\$|PRN|AUX|NUL|CON|COM[0-9]|LPT[0-9])$/i.test(name) ||
          ['.syncinfo', 'thumbs.db', 'desktop.ini', 'sync.cache', '$recycle.bin'].includes(name.toLowerCase()) ||
          name.startsWith('.~') ||
          name.startsWith('~') ||
          name.includes('~$') ||
          /^(PRN\.[a-z0-9]+|AUX\.[a-z0-9]+|NUL\.[a-z0-9]+|CON\.[a-z0-9]+|COM[0-9]\.[a-z0-9]+|LPT[0-9]\.[a-z0-9]+)$/i.test(
            name,
          );
  }

  public hasIllegalCharacters(name: string): boolean {
    return !this.illegalCharacters(name);
  }

  public isExist(name: string, pathlist: any[]): boolean {
    if (!pathlist || this.isEmpty(name)) {
      return false;
    }
    return pathlist.some((val) => this.nameNormalize.equalsByName(val.name, name));
  }
}
